import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext.jsx';
import { TenantBrandingProvider } from './context/TenantBrandingContext.jsx';
import './App.scss';
import AnonymousWebLayout from './layouts/AnonymousWebLayout.jsx';
import AnonymousWebLayout18yitw0 from './layouts/AnonymousWebLayout18yitw0.jsx';
import TenantUsersAccessManagementLayout from './layouts/TenantUsersAccessManagementLayout.jsx';
import SpatialHierarchyPropertyTreeLayout from './layouts/SpatialHierarchyPropertyTreeLayout.jsx';
import BatchQrCodeGeneratorAssetPlacardsLayout from './layouts/BatchQrCodeGeneratorAssetPlacardsLayout.jsx';
import RolePermissionsSecurityPoliciesLayout from './layouts/RolePermissionsSecurityPoliciesLayout.jsx';
import DefaultWebLayout from './layouts/DefaultWebLayout.jsx';
import DefaultWebLayoutCevls9 from './layouts/DefaultWebLayoutCevls9.jsx';
import DefaultWebLayoutQmfpft from './layouts/DefaultWebLayoutQmfpft.jsx';
import WorkOrderPackagingDispatchBoardLayout from './layouts/WorkOrderPackagingDispatchBoardLayout.jsx';
import ProviderProfileAccreditationManagementLayout from './layouts/ProviderProfileAccreditationManagementLayout.jsx';
import OperationalSlaMaintenanceDashboardLayout from './layouts/OperationalSlaMaintenanceDashboardLayout.jsx';
import TradeCategoriesRateCardsLayout from './layouts/TradeCategoriesRateCardsLayout.jsx';
import InspectionChecklistsTaskTemplateBuilderLayout from './layouts/InspectionChecklistsTaskTemplateBuilderLayout.jsx';
import DefaultWebLayoutMs5erx from './layouts/DefaultWebLayoutMs5erx.jsx';
import DefaultWebLayout1n801y6 from './layouts/DefaultWebLayout1n801y6.jsx';
import DefaultWebLayout3rhmrt from './layouts/DefaultWebLayout3rhmrt.jsx';
import SubscriptionTierSeatLicensingLayout from './layouts/SubscriptionTierSeatLicensingLayout.jsx';
import ComplianceAuditTrailExplorerLayout from './layouts/ComplianceAuditTrailExplorerLayout.jsx';
import DefaultWebLayoutRoq9d5 from './layouts/DefaultWebLayoutRoq9d5.jsx';
import OperatorShell from './layouts/OperatorShell.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleRoute from './components/RoleRoute.jsx';
import HomeRedirect from './components/HomeRedirect.jsx';
import { ROUTE_ROLES, OPERATOR_ROLES } from './config/routeAccess.js';
import { UnauthorizedPage, ForbiddenPage, NotFoundPage, ServerErrorPage } from './pages/status/index.jsx';

// Pages and layouts are generated per the design reference. Lazy-import them and
// wire their routes here, e.g.:
//
//   import { lazy } from 'react';
//   import { Route } from 'react-router-dom';
//   import ProtectedRoute from './components/ProtectedRoute.jsx';
//   const HomePage = lazy(() => import('./pages/HomePage.jsx'));
//
//   <Routes>
//     <Route path="/" element={<HomePage />} />
//     <Route element={<ProtectedRoute />}>
//       <Route path="/dashboard" element={<DashboardPage />} />
//     </Route>
//   </Routes>

const Loader = () => (
  <div className="d-flex justify-content-center py-5">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading…</span>
    </div>
  </div>
);

const SignInPage = lazy(() => import('./pages/sign-in/index.jsx'));
const SelfRegistrationPage = lazy(() => import('./pages/self-registration/index.jsx'));
const ForgotPasswordPage = lazy(() => import('./pages/forgot-password/index.jsx'));
const ResetPasswordPage = lazy(() => import('./pages/reset-password/index.jsx'));
const SubscriptionTierSeatLicensingPage = lazy(() => import('./pages/subscription-tier-seat-licensing/index.jsx'));
const InvoicingStripeBillingPortalPage = lazy(() => import('./pages/invoicing-stripe-billing-portal/index.jsx'));
const SystemIntegrationsWebhooksMonitorPage = lazy(() => import('./pages/system-integrations-webhooks-monitor/index.jsx'));
const RolePermissionsPage = lazy(() => import('./pages/role-permissions-security-policies/index.jsx'));
const TenantUsersPage = lazy(() => import('./pages/tenant-users-access-management/index.jsx'));
const SpatialTreePage = lazy(() => import('./pages/spatial-hierarchy-property-tree/index.jsx'));
const BatchQrPlacardsPage = lazy(() => import('./pages/batch-qr-code-generator-asset-placards/index.jsx'));
const QrDefectIntakePage = lazy(() => import('./pages/qr-defect-intake-pwa/index.jsx'));
const ResidentPortalPage = lazy(() => import('./pages/resident-portal-defect-submission/index.jsx'));
const ServiceDeskPage = lazy(() => import('./pages/service-desk-call-email-intake/index.jsx'));
const KanbanTriageBoardPage = lazy(() => import('./pages/kanban-triage-sla-board/index.jsx'));
const WorkOrderBoardPage = lazy(() => import('./pages/work-order-packaging-dispatch-board/index.jsx'));
const ProviderProfilePage = lazy(() => import('./pages/provider-profile-accreditation-management/index.jsx'));
const TradeRateCardsPage = lazy(() => import('./pages/trade-categories-rate-cards/index.jsx'));
const ContractorQuotationBuilderPage = lazy(() => import('./pages/contractor-quotation-builder/index.jsx'));
const QuotationApprovalsQueuePage = lazy(() => import('./pages/multi-tier-quotation-approvals-queue/index.jsx'));
const TechnicianExecutionPage = lazy(() => import('./pages/technician-mobile-execution-pwa/index.jsx'));
const DualSignOffPage = lazy(() => import('./pages/dual-sign-off-resident-verification/index.jsx'));
const SlaDashboardPage = lazy(() => import('./pages/operational-sla-maintenance-dashboard/index.jsx'));
const ProviderBenchmarkScorecardPage = lazy(() => import('./pages/provider-benchmark-scorecard/index.jsx'));
const ComplianceAuditTrailExplorerPage = lazy(() => import('./pages/compliance-audit-trail-explorer/index.jsx'));
const InspectionTemplatesPage = lazy(() => import('./pages/inspection-checklists-task-template-builder/index.jsx'));
const TenantBrandingPage = lazy(() => import('./pages/tenant-configuration-custom-branding/index.jsx'));
const AssetOsIntegrationPage = lazy(() => import('./pages/assetos-integration/index.jsx'));
const ResidentDirectoryPage = lazy(() => import('./pages/resident-directory/index.jsx'));
const ResidentChargesPage = lazy(() => import('./pages/resident-charges/index.jsx'));
const InspectionsPage = lazy(() => import('./pages/inspections/index.jsx'));
const InspectionWalkPage = lazy(() => import('./pages/inspections/InspectionWalk.jsx'));
const OperatorCustomersPage = lazy(() => import('./pages/operator/index.jsx'));
const OperatorCustomerDetailPage = lazy(() => import('./pages/operator/CustomerDetail.jsx'));
const OperatorPlansPage = lazy(() => import('./pages/operator/PlansPage.jsx'));
const OperatorActivityPage = lazy(() => import('./pages/operator/Activity.jsx'));
const TrackTicketPage = lazy(() => import('./pages/track-ticket/index.jsx'));
const VerifyEmailPage = lazy(() => import('./pages/verify-email/index.jsx'));

// /sign-up?token=... is an alias of the slug route /self-registration (query string preserved).
function SignUpRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/self-registration${search}`} replace />;
}

// /intake/qr?loc=<token> is an alias of the slug route /qr-defect-intake-pwa (query string preserved).
function IntakeQrRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/qr-defect-intake-pwa${search}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <TenantBrandingProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            {/* Root: signed-in users go to their role's landing page, everyone else to sign-in */}
            <Route path="/" element={<HomeRedirect />} />
            {/* Public (Anonymous) routes */}
            <Route element={<AnonymousWebLayout />}>
              <Route path="/sign-in" element={<SignInPage />} />
              <Route path="/self-registration" element={<SelfRegistrationPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/track/:ticketNumber" element={<TrackTicketPage />} />
            </Route>
            {/* QR Defect Intake PWA: public; a scanned placard (/q/:token) lands directly on the intake form */}
            <Route element={<AnonymousWebLayout18yitw0 />}>
              <Route path="/qr-defect-intake-pwa" element={<QrDefectIntakePage />} />
              <Route path="/q/:token" element={<QrDefectIntakePage />} />
            </Route>
            <Route path="/intake/qr" element={<IntakeQrRedirect />} />
            {/* Admin-only: signed-in required (-> /sign-in), then role-gated (-> /403) */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['role-permissions-security-policies']} />}>
                <Route element={<RolePermissionsSecurityPoliciesLayout />}>
                  <Route path="/role-permissions-security-policies" element={<RolePermissionsPage />} />
                  <Route path="/system-integrations-webhooks-monitor" element={<SystemIntegrationsWebhooksMonitorPage />} />
                </Route>
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['tenant-users-access-management']} />}>
                <Route element={<TenantUsersAccessManagementLayout />}>
                  <Route path="/tenant-users-access-management" element={<TenantUsersPage />} />
                </Route>
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['spatial-hierarchy-property-tree']} />}>
                <Route element={<SpatialHierarchyPropertyTreeLayout />}>
                  <Route path="/spatial-hierarchy-property-tree" element={<SpatialTreePage />} />
                </Route>
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['batch-qr-code-generator-asset-placards']} />}>
                <Route element={<BatchQrCodeGeneratorAssetPlacardsLayout />}>
                  <Route path="/batch-qr-code-generator-asset-placards" element={<BatchQrPlacardsPage />} />
                </Route>
              </Route>
            </Route>
            {/* Resident Portal: Raiser (resident / unit owner) only */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['resident-portal-defect-submission']} />}>
                <Route element={<DefaultWebLayoutCevls9 />}>
                  <Route path="/resident-portal-defect-submission" element={<ResidentPortalPage />} />
                </Route>
              </Route>
            </Route>
            {/* Service Desk Console: Service Desk Agent and supervising roles */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['service-desk-call-email-intake']} />}>
                <Route element={<DefaultWebLayoutQmfpft />}>
                  <Route path="/service-desk-call-email-intake" element={<ServiceDeskPage />} />
                </Route>
              </Route>
            </Route>
            {/* Resident Directory: Customer Admin / Manager manage; Supervisor / Service Desk Agent read-only */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['resident-directory']} />}>
                <Route element={<DefaultWebLayout />}>
                  <Route path="/resident-directory" element={<ResidentDirectoryPage />} />
                </Route>
              </Route>
            </Route>
            {/* Resident charges (chargeable repairs): Supervisor / Manager / Customer Admin act; Finance takes payments; Service Desk read-only */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['resident-charges']} />}>
                <Route element={<DefaultWebLayout1n801y6 />}>
                  <Route path="/resident-charges" element={<ResidentChargesPage />} />
                </Route>
              </Route>
            </Route>
            {/* Inspections: managers schedule and review; they and the assigned technician carry out the walk-through */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES.inspections} />}>
                <Route element={<DefaultWebLayout3rhmrt />}>
                  <Route path="/inspections" element={<InspectionsPage />} />
                  <Route path="/inspections/:id" element={<InspectionWalkPage />} />
                </Route>
              </Route>
            </Route>
            {/* Kanban Triage & SLA Board: Supervisor and supervising roles */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['kanban-triage-sla-board']} />}>
                <Route element={<DefaultWebLayout />}>
                  <Route path="/kanban-triage-sla-board" element={<KanbanTriageBoardPage />} />
                </Route>
              </Route>
            </Route>
            {/* Work Order Packaging & Dispatch Board: Supervisor and supervising roles */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['work-order-packaging-dispatch-board']} />}>
                <Route element={<WorkOrderPackagingDispatchBoardLayout />}>
                  <Route path="/work-order-packaging-dispatch-board" element={<WorkOrderBoardPage />} />
                </Route>
              </Route>
            </Route>
            {/* Provider Profile & Accreditation Management: Provider Admin only (own contractor organization) */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['provider-profile-accreditation-management']} />}>
                <Route element={<ProviderProfileAccreditationManagementLayout />}>
                  <Route path="/provider-profile-accreditation-management" element={<ProviderProfilePage />} />
                </Route>
              </Route>
            </Route>
            {/* Trade Categories & Rate Cards: Provider Admin configures; Customer Admin / platform Admin review */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['trade-categories-rate-cards']} />}>
                <Route element={<TradeCategoriesRateCardsLayout />}>
                  <Route path="/trade-categories-rate-cards" element={<TradeRateCardsPage />} />
                </Route>
              </Route>
            </Route>
            {/* Contractor Quotation Builder: Provider Admin only (own contractor organization) */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['contractor-quotation-builder']} />}>
                <Route element={<DefaultWebLayoutMs5erx />}>
                  <Route path="/contractor-quotation-builder" element={<ContractorQuotationBuilderPage />} />
                </Route>
              </Route>
            </Route>
            {/* Multi-Tier Quotation Approvals (GV-06): reviewers whose delegation covers Tier A/B/C */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['multi-tier-quotation-approvals-queue']} />}>
                <Route element={<DefaultWebLayout1n801y6 />}>
                  <Route path="/multi-tier-quotation-approvals-queue" element={<QuotationApprovalsQueuePage />} />
                </Route>
              </Route>
            </Route>
            {/* Technician Mobile Execution PWA (GV-03): field Worker only; works offline from IndexedDB */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['technician-mobile-execution-pwa']} />}>
                <Route element={<DefaultWebLayout3rhmrt />}>
                  <Route path="/technician-mobile-execution-pwa" element={<TechnicianExecutionPage />} />
                </Route>
              </Route>
            </Route>
            {/* Dual Sign-Off & Resident Verification (GV-07 / GV-08): field Worker signs off, Raiser verifies and rates */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['dual-sign-off-resident-verification']} />}>
                <Route element={<DefaultWebLayoutRoq9d5 />}>
                  <Route path="/dual-sign-off-resident-verification" element={<DualSignOffPage />} />
                </Route>
              </Route>
            </Route>
            {/* Inspection Checklists & Task Template Builder: Supervisor authors; Customer Admin / Admin manage */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['inspection-checklists-task-template-builder']} />}>
                <Route element={<InspectionChecklistsTaskTemplateBuilderLayout />}>
                  <Route path="/inspection-checklists-task-template-builder" element={<InspectionTemplatesPage />} />
                </Route>
              </Route>
            </Route>
            {/* Operational SLA & Maintenance Dashboard (GV-02 / GV-09): Manager oversight, also Supervisor / Admin */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['operational-sla-maintenance-dashboard']} />}>
                <Route element={<OperationalSlaMaintenanceDashboardLayout />}>
                  <Route path="/operational-sla-maintenance-dashboard" element={<SlaDashboardPage />} />
                </Route>
              </Route>
            </Route>
            {/* Provider Benchmark Scorecard (GV-10): Manager contractor benchmarking, also Supervisor / Admin */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['provider-benchmark-scorecard']} />}>
                <Route element={<WorkOrderPackagingDispatchBoardLayout />}>
                  <Route path="/provider-benchmark-scorecard" element={<ProviderBenchmarkScorecardPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="/analytics/provider-scorecards" element={<Navigate to="/provider-benchmark-scorecard" replace />} />
            <Route path="/analytics/sla-dashboard" element={<Navigate to="/operational-sla-maintenance-dashboard" replace />} />
            {/* Compliance Audit Trail Explorer (GV-14): Auditor, also tenant / platform administrators */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['compliance-audit-trail-explorer']} />}>
                <Route element={<ComplianceAuditTrailExplorerLayout />}>
                  <Route path="/compliance-audit-trail-explorer" element={<ComplianceAuditTrailExplorerPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="/audit/compliance-export" element={<Navigate to="/compliance-audit-trail-explorer" replace />} />
            {/* Subscription Tier & Seat Licensing (GV-13): Finance manages plan and seats; Customer Admin may also upgrade */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['subscription-tier-seat-licensing']} />}>
                <Route element={<SubscriptionTierSeatLicensingLayout />}>
                  <Route path="/subscription-tier-seat-licensing" element={<SubscriptionTierSeatLicensingPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="/billing/subscriptions" element={<Navigate to="/subscription-tier-seat-licensing" replace />} />
            {/* Invoicing & Stripe Billing Portal: Finance (and Customer Admin) manage payment methods, invoices and dunning */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['invoicing-stripe-billing-portal']} />}>
                <Route element={<SubscriptionTierSeatLicensingLayout />}>
                  <Route path="/invoicing-stripe-billing-portal" element={<InvoicingStripeBillingPortalPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="/billing/invoices" element={<Navigate to="/invoicing-stripe-billing-portal" replace />} />
            {/* Tenant Configuration & Custom Branding: tenant / platform administrators */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['tenant-configuration-custom-branding']} />}>
                <Route element={<TenantUsersAccessManagementLayout />}>
                  <Route path="/tenant-configuration-custom-branding" element={<TenantBrandingPage />} />
                </Route>
              </Route>
            </Route>
            {/* AssetOS add-on: whoever manages the tenant's settings pairs properties with AssetOS */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={ROUTE_ROLES['assetos-integration']} />}>
                <Route element={<TenantUsersAccessManagementLayout />}>
                  <Route path="/assetos-integration" element={<AssetOsIntegrationPage />} />
                </Route>
              </Route>
            </Route>
            {/* Xamun Operator console: the vendor's own area, with its own shell. Every other role gets /403. */}
            <Route element={<ProtectedRoute />}>
              <Route element={<RoleRoute roles={OPERATOR_ROLES} />}>
                <Route element={<OperatorShell />}>
                  <Route path="/admin" element={<OperatorCustomersPage />} />
                  <Route path="/admin/customers/:id" element={<OperatorCustomerDetailPage />} />
                  <Route path="/admin/plans" element={<OperatorPlansPage />} />
                  <Route path="/admin/activity" element={<OperatorActivityPage />} />
                </Route>
              </Route>
            </Route>
            <Route path="/work-orders" element={<Navigate to="/work-order-packaging-dispatch-board" replace />} />
            <Route path="/triage" element={<Navigate to="/kanban-triage-sla-board" replace />} />
            <Route path="/servicedesk/quick-intake" element={<Navigate to="/service-desk-call-email-intake" replace />} />
            <Route path="/resident/new-ticket" element={<Navigate to="/resident-portal-defect-submission" replace />} />
            <Route path="/resident/my-tickets" element={<Navigate to="/resident-portal-defect-submission" replace />} />
            <Route path="/locations/qr-generator" element={<Navigate to="/batch-qr-code-generator-asset-placards" replace />} />
            <Route path="/locations/tree" element={<Navigate to="/spatial-hierarchy-property-tree" replace />} />
            <Route path="/locations" element={<Navigate to="/spatial-hierarchy-property-tree" replace />} />
            <Route path="/tenant-users" element={<Navigate to="/tenant-users-access-management" replace />} />
            <Route path="/admin/webhooks" element={<Navigate to="/system-integrations-webhooks-monitor" replace />} />
            <Route path="/roles-permissions" element={<Navigate to="/role-permissions-security-policies" replace />} />
            <Route path="/sign-up" element={<SignUpRedirect />} />
            <Route path="/login" element={<Navigate to="/sign-in" replace />} />
            <Route path="/401" element={<UnauthorizedPage />} />
            <Route path="/403" element={<ForbiddenPage />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="/500" element={<ServerErrorPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </TenantBrandingProvider>
    </AuthProvider>
  );
}

export default App;
